import type { PcmAudio } from "../types";
import { decodeAudioFile } from "./decode";
import { chooseCommonSampleRate, mergeSequential } from "./merge";
import { resampleAudio } from "./resample";

/** Sample rate most speech-to-text models expect; halves data vs. 32 kHz. */
export const SPEECH_SAMPLE_RATE = 16000;

export interface PrepareForTranscriptionOptions {
  /** Target sample rate. Defaults to {@link SPEECH_SAMPLE_RATE} (16 kHz). */
  sampleRate?: number;
  /** Downmix to mono. Defaults to true (speech gains nothing from stereo). */
  mono?: boolean;
}

/**
 * Decode -> (mono) -> resample into transcription-ready PCM. Returns the decoded
 * buffer; the caller encodes it (WAV/Opus) — keeping process and encode separate
 * so the UI can offer the result in multiple formats without reprocessing.
 */
export async function prepareForTranscription(
  input: Blob | ArrayBuffer,
  options: PrepareForTranscriptionOptions = {}
): Promise<PcmAudio> {
  const sampleRate = options.sampleRate ?? SPEECH_SAMPLE_RATE;
  const mono = options.mono ?? true;

  const decoded = await decodeAudioFile(input);
  return resampleAudio(decoded, { sampleRate, ...(mono ? { channels: 1 } : {}) });
}

export interface LoadAndMergeOptions {
  /** Target sample rate. Defaults to the highest among the inputs. */
  sampleRate?: number;
}

/**
 * Decode several files, reconcile them to a common sample rate, and concatenate
 * in order into one PCM buffer. Clips already at the target rate aren't
 * resampled; differing channel counts are reconciled by {@link mergeSequential}.
 */
export async function loadAndMergeAudio(
  inputs: (Blob | ArrayBuffer)[],
  options: LoadAndMergeOptions = {}
): Promise<PcmAudio> {
  if (inputs.length === 0) throw new Error("loadAndMergeAudio: no files provided");

  const decoded = await Promise.all(inputs.map((input) => decodeAudioFile(input)));
  const targetRate =
    options.sampleRate ?? chooseCommonSampleRate(decoded.map((clip) => clip.sampleRate));

  const reconciled = await Promise.all(
    decoded.map((clip) =>
      clip.sampleRate === targetRate ? clip : resampleAudio(clip, { sampleRate: targetRate })
    )
  );

  return mergeSequential(reconciled);
}

/** Re-export so callers can build their own pipelines from one import. */
export type { PcmAudio };
